import { Box, Flex, Spinner, Stack, Text } from "@chakra-ui/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import {
  apiRequest,
  AUTHORIZATION_REQUIRED_EVENT,
} from "./api-client";

export type AuthStatus = "checking" | "authenticated" | "anonymous";

export type AdminSession = {
  ok: boolean;
  expiresAt?: string;
};

type AuthSessionValue = {
  status: AuthStatus;
  session?: AdminSession;
  checkError?: string;
  login(secret: string): Promise<AdminSession>;
  logout(): Promise<void>;
  checkSession(): Promise<boolean>;
};

const AuthSessionContext = createContext<AuthSessionValue | undefined>(undefined);

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [session, setSession] = useState<AdminSession>();
  const [checkError, setCheckError] = useState<string>();

  const checkSession = useCallback(async () => {
    try {
      const current = await apiRequest<AdminSession>("/trading/admin/check");
      setSession(current);
      setCheckError(undefined);
      setStatus("authenticated");
      return true;
    } catch (error) {
      const statusCode = (error as { statusCode?: number }).statusCode;
      setSession(undefined);
      setStatus("anonymous");
      setCheckError(
        statusCode === 401
          ? undefined
          : error instanceof Error
            ? error.message
            : String(error),
      );
      return false;
    }
  }, []);

  useEffect(() => {
    void checkSession();
    const expire = () => {
      setSession(undefined);
      setCheckError(undefined);
      setStatus("anonymous");
    };
    window.addEventListener(AUTHORIZATION_REQUIRED_EVENT, expire);
    return () => window.removeEventListener(AUTHORIZATION_REQUIRED_EVENT, expire);
  }, [checkSession]);

  const login = useCallback(async (secret: string) => {
    const current = await apiRequest<AdminSession>("/trading/admin/session", {
      method: "POST",
      headers: { "X-Trading-Admin-Token": secret },
    });
    setSession(current);
    setCheckError(undefined);
    setStatus("authenticated");
    return current;
  }, []);

  const logout = useCallback(async () => {
    await apiRequest<{ ok: boolean }>("/trading/admin/session", { method: "DELETE" });
    setSession(undefined);
    setCheckError(undefined);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthSessionValue>(
    () => ({ status, session, checkError, login, logout, checkSession }),
    [checkError, checkSession, login, logout, session, status],
  );

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

export function useAuthSession(): AuthSessionValue {
  const value = useContext(AuthSessionContext);
  if (!value) throw new Error("useAuthSession must be used inside AuthSessionProvider");
  return value;
}

export function ProtectedRoute() {
  const { status } = useAuthSession();
  const location = useLocation();
  if (status === "checking") return <SessionLoadingScreen />;
  if (status === "anonymous") {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

export function SessionLoadingScreen() {
  return (
    <Flex minH="100vh" align="center" justify="center" bg="bg.canvas" color="fg.default">
      <Stack gap="3" align="center">
        <Flex boxSize="44px" align="center" justify="center" bg="bg.panel" borderWidth="1px" borderColor="border.default" borderRadius="4px">
          <Spinner size="sm" color="brand.400" />
        </Flex>
        <Box textAlign="center">
          <Text fontSize="sm" fontWeight="750">Verifying secure session</Text>
          <Text mt="1" fontSize="xs" color="fg.muted">Connecting to the trading server…</Text>
        </Box>
      </Stack>
    </Flex>
  );
}
